import { PlusOutlined } from '@ant-design/icons';
import { useMutation } from '@apollo/client/react';
import {
  DndContext,
  KeyboardSensor,
  MouseSensor,
  TouchSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import { restrictToHorizontalAxis } from '@dnd-kit/modifiers';
import {
  arrayMove,
  horizontalListSortingStrategy,
  SortableContext,
  useSortable,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import {
  Alert,
  Badge,
  Button,
  Empty,
  Form,
  Image,
  message,
  Upload,
} from 'antd';
import clsx from 'clsx';
import { debounce, filter, includes, isArray, map, take } from 'lodash';
import moment from 'moment';
import React, {
  forwardRef,
  useCallback,
  useContext,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useMedia } from 'react-use';
import { AppContext } from '../../../../../../AppContext';
import {
  CommentBlackIcon,
  DeleteButton,
  DragIcon,
  EditButton,
} from '../../../../../../assets/svg';
import useLazyQueryWithCallbacks from '../../../../../../common/CommonUseLazyQuery';
import {
  ALLOWED_ACTION_KEYS,
  ALLOWED_ACTION_TYPE,
  ALLOWED_IMAGE_TYPES,
  API_TO_AGGREGATE_FUNCTION_LABELS,
  BREAKPOINTS,
  DATETIMEWITHDIVIDE,
  DEFAULT_PAGE_SIZE,
  DEFAULTDATEFORMAT,
  ELEMENT_IDS,
  FALLBACK_PDF_URL,
  FIELD_TYPES,
  FILE_SIZE_8MB,
  INVALID_FIELD_VALUES,
  PROJECT_ROLES,
  REGEX,
  REGISTER_DETAILS_TYPES,
  REGISTER_LOG_STATUS,
  ROUTES,
  SCROLL_PERCENT,
  TAB_KEYS,
  TIME_12_HOUR_FORMAT,
  TIME_24_HOUR_FORMAT,
  USER_ROLES,
} from '../../../../../../common/constants';
import useRouter from '../../../../../../common/useRouter';
import {
  addWatermark,
  calculateFieldValue,
  compressImage,
  dataURLtoFile,
  fileUpload,
  formatNumericInput,
  toCamelCase,
} from '../../../../../../common/utils';
import AggregateDropdown, {
  createAggregateHandlers,
  processRegisterStatsResults,
} from '../../../../../../components/AggregateDropdown';
import CanPerform from '../../../../../../components/CanPerform';
import CommonTable from '../../../../../../components/CommonTable';
import CommonTooltip from '../../../../../../components/CommonTooltip';
import Portal from '../../../../../../components/Portal';
import SearchComponent from '../../../../../../components/SearchComponent';
import { GET_URLS } from '../../../../../approver/graphql/Queries';
import {
  CREATE_REGISTER_TABLE_FIELDS,
  UPDATE_REGISTER_FORM_FIELD_SEQUENCES,
  UPDATE_REGISTER_LOG_DETAIL_ENTRY,
} from '../../../../graphql/Mutation';
import {
  GET_REGISTER,
  GET_REGISTER_LOG_FORM_FIELDS_ENTRY_LIST,
  REGISTER_STATS,
} from '../../../../graphql/Queries';
import AddEditTableFieldModal from './AddEditTableFieldModal';
import AddFieldModal from './AddFieldModal';
import AdditionalCommentsModal from './AdditionalCommentsModal';
import DeleteFormFieldRecordModal from './DeleteFormFieldRecordModal';
import FieldIcon from './FieldIcon';
import RegisterCommentModal from './RegisterCommentModal';
import DeleteTableFieldModal from './registerDetails/DeleteTableFieldModal';
import RenderFields from './RenderFields';

const ColumnContext = React.createContext({});

const EditableCell = ({
  record,
  dataIndex,
  title,
  fieldId,
  fieldType,
  editing,
  isRequired,
  options = [],
  index,
  fieldsData,
  onFieldClick,
  isFieldClicked,
  children,
  isMobileViewport,
  isAuditor,
  isProjectAdminOrInspector,
  isOptionsFocused,
  setIsOptionsFocused,
  isMultiSelectOptionsFocused,
  setIsMultiSelectOptionsFocused,
  isDateFocused,
  setIsDateFocused,
  onDateOpenChange,
  isTimeFocused,
  setIsTimeFocused,
  onTimeOpenChange,
  isDisabled,
  handleFieldChange,
  handleBlurSave,
  handleFileListRefUpdate,
  form,
  tableHeaders,
  fileListRef,
  deletingFile,
  setDeletingFile,
  uploadKey,
  handleOnChange,
  handlePreview,
  handleUploadRemove,
  ...restProps
}) => {
  const fieldOptions = useMemo(
    () =>
      map(options, (option) => ({
        label: option,
        value: option,
      })),
    [options],
  );

  const fieldName = `${index}_${dataIndex}`;

  const handleTextChange = useCallback(
    (e) => {
      handleFieldChange({
        [`${index}_${dataIndex}`]: e?.target?.value,
      });
    },
    [handleFieldChange, index, dataIndex],
  );

  const handleTextBlur = useCallback(() => {
    if (isMobileViewport) return;
    handleBlurSave(index, record);
  }, [isMobileViewport, handleBlurSave, index, record]);

  const handleNumericChange = useCallback(
    (e) => {
      const value = e?.target?.value;
      const formattedValue = formatNumericInput(value);
      handleFieldChange({
        [`${index}_${dataIndex}`]: formattedValue,
      });
    },
    [handleFieldChange, index, dataIndex],
  );

  const handleUploadChange = (info) => {
    const isRemoveOperation =
      info?.fileList?.length < fileListRef?.current[fieldName]?.length;

    if (isRemoveOperation) {
      const newFileList = info?.fileList?.map((file) => ({
        ...file,
        status: 'done',
      }));
      handleFileListRefUpdate(newFileList, fieldName);

      if (fieldsData?.current?.[index]) {
        // eslint-disable-next-line no-param-reassign
        fieldsData.current[index] = {
          ...fieldsData?.current[index],
          [index]: newFileList?.map((file) => ({
            uid: file?.uid,
            status: file?.status,
            url: file?.url,
            value: file?.url,
            key: file?.key,
            name: file?.name,
            thumbUrl: file?.thumbUrl,
          })),
        };
      }
      form?.setFieldsValue({
        [fieldName]: newFileList,
      });
      return;
    }
    const newFileList = info?.fileList?.slice(-3)?.map((file) => ({
      ...file,
      status: 'done',
    }));
    if (newFileList?.length > 3) {
      message.warning('Maximum 3 files are allowed');
      return;
    }
    handleFileListRefUpdate(newFileList, fieldName);

    if (fieldsData?.current?.[index]) {
      // eslint-disable-next-line no-param-reassign
      fieldsData.current[index] = {
        ...fieldsData?.current[index],
        [index]: newFileList?.map((file) => ({
          uid: file?.uid,
          status: file?.status,
          url: file?.url,
          value: file?.url,
          key:
            file?.key ||
            (file?.name ? `register/attachments/${file?.name}` : undefined),
          name: file?.name,
          thumbUrl: file?.thumbUrl,
        })),
      };
    }

    const hasNewFiles = newFileList?.some((file) => !file?.url && !file?.key);
    if (hasNewFiles) {
      handleOnChange(info, fieldName, record, dataIndex, index);
    }
  };

  return (
    <td {...restProps}>
      {editing ? (
        <RenderFields
          fieldType={fieldType}
          fieldIndex={`${index}_${dataIndex}`}
          fieldIsRequired={isRequired && !record?.isEmptyRow}
          className="m-0"
          initialFieldValue={record[dataIndex]}
          fieldDisabled={isAuditor || isDisabled || !isProjectAdminOrInspector}
          divClassName="width-200"
          onTextChange={handleTextChange}
          onTextBlur={handleTextBlur}
          onFieldClick={onFieldClick}
          isFieldClicked={isFieldClicked}
          options={fieldOptions}
          onNumericChange={handleNumericChange}
          onNumericBlur={() => {
            if (isMobileViewport) return;
            handleBlurSave(index, record);
          }}
          dateOpen={isDateFocused === `${index}_${dataIndex}`}
          onDateFocused={() => {
            if (!isMobileViewport) {
              setIsDateFocused(`${index}_${dataIndex}`);
              setIsTimeFocused(null);
              setIsOptionsFocused(null);
              setIsMultiSelectOptionsFocused(null);
            }
          }}
          onDateOpenChange={onDateOpenChange}
          onDateChange={(date) => {
            handleFieldChange({
              [`${index}_${dataIndex}`]: date,
            });
          }}
          timeOpen={isTimeFocused === `${index}_${dataIndex}`}
          onTimeFocused={() => {
            if (!isMobileViewport) {
              setIsTimeFocused(`${index}_${dataIndex}`);
              setIsDateFocused(null);
              setIsOptionsFocused(null);
              setIsMultiSelectOptionsFocused(null);
            }
          }}
          onTimeOpenChange={onTimeOpenChange}
          onTimeChange={(time) => {
            handleFieldChange({
              [`${index}_${dataIndex}`]: time,
            });
            handleBlurSave(index, record);
          }}
          onTimeBlur={() => {
            if (isMobileViewport) return;
            handleBlurSave(index, record);
          }}
          optionsOpen={isOptionsFocused === `${index}_${dataIndex}`}
          onOptionsFocus={() => {
            if (!isMobileViewport) {
              setIsOptionsFocused(`${index}_${dataIndex}`);
              setIsDateFocused(null);
              setIsTimeFocused(null);
              setIsMultiSelectOptionsFocused(null);
            }
          }}
          onOptionBlur={() => {
            if (!isMobileViewport) {
              setIsOptionsFocused(null);
            }
          }}
          popupClassName={isMobileViewport && 'd-none'}
          onOptionChange={(value) => {
            setIsOptionsFocused(null);
            handleFieldChange({
              [`${index}_${dataIndex}`]: value || undefined,
            });
            handleBlurSave(index, record);
          }}
          onOptionOpenChange={(open) => {
            if (!open) {
              setIsOptionsFocused(null);
            } else {
              setIsOptionsFocused(`${index}_${dataIndex}`);
            }
          }}
          optionClassName="form-field-select"
          multiSelectOpen={
            isMultiSelectOptionsFocused === `${index}_${dataIndex}`
          }
          onMultiSelectFocus={() => {
            if (!isMobileViewport) {
              setIsMultiSelectOptionsFocused(`${index}_${dataIndex}`);
              setIsDateFocused(null);
              setIsTimeFocused(null);
              setIsOptionsFocused(null);
            }
          }}
          onMultiSelectBlur={() => {
            if (!isMobileViewport) {
              setIsMultiSelectOptionsFocused(null);
              handleBlurSave(index, record);
            }
          }}
          onMultiSelectOpenChange={(open) => {
            if (!open) {
              setIsMultiSelectOptionsFocused(null);
            } else {
              setIsMultiSelectOptionsFocused(`${index}_${dataIndex}`);
            }
          }}
          onMultiSelectChange={(value) => {
            const newValue = value?.length > 0 ? value : undefined;
            handleFieldChange({ [`${index}_${dataIndex}`]: newValue });
          }}
          getPopupContainer={() =>
            // eslint-disable-next-line no-undef
            document.querySelector('.ant-table-content')
          }
          formClassName="m-0"
          calculateFieldValue={() =>
            calculateFieldValue(`${index}_${dataIndex}`, form, tableHeaders)
          }
          onCalculationChange={(e) =>
            handleFieldChange({
              [`${index}_${dataIndex}`]: e?.target?.value,
            })
          }
          onCalculationBlur={() => {
            if (isMobileViewport) return;
            handleBlurSave(index, record);
          }}
          fileListRef={fileListRef}
          openFileDialogOnClick={!isMobileViewport}
          records={record}
          uploadDataIndex={dataIndex}
          uploadIndex={index}
          uploadFieldName={`${index}_${dataIndex}`}
          handleUploadChange={handleUploadChange}
          uploadKey={`${uploadKey}-${fieldName}`}
          beforeUpload={(file) => {
            const currentFiles = fileListRef?.current[fieldName] || [];
            if (currentFiles?.length >= 3) {
              message.warning('Maximum 3 files are allowed');
              return Upload.LIST_IGNORE;
            }
            if (file?.size > FILE_SIZE_8MB) {
              message.destroy();
              message.error('Maximum file size is 8MB');
              return Upload.LIST_IGNORE;
            }
            const newFile = file;
            if (file?.type?.startsWith('image/')) {
              newFile.thumbUrl = URL?.createObjectURL(file);
            } else if (file?.type === 'application/pdf') {
              newFile.thumbUrl = FALLBACK_PDF_URL;
            }
            return false;
          }}
          onUploadRemove={handleUploadRemove}
        />
      ) : (
        <div>{children}</div>
      )}
    </td>
  );
};

const Columns = (props) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    setActivatorNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({
    id: props?.column?.id || props?.dataIndex,
  });

  const style = {
    ...props?.style,
    transform: CSS.Translate?.toString(transform),
    transition,
    ...(isDragging
      ? {
          position: 'relative',
          zIndex: 9999,
        }
      : {}),
  };

  const contextValue = useMemo(
    () => ({
      setActivatorNodeRef,
      listeners,
    }),
    [setActivatorNodeRef, listeners],
  );

  return (
    <ColumnContext.Provider value={contextValue}>
      <th {...props} ref={setNodeRef} style={style} {...attributes} />
    </ColumnContext.Provider>
  );
};

const DragHandle = () => {
  const { setActivatorNodeRef, listeners } = useContext(ColumnContext);
  return (
    <CanPerform
      action={ALLOWED_ACTION_KEYS.ADD_PROJECT_REGISTER}
      type={ALLOWED_ACTION_TYPE.BOTH}
    >
      <CommonTooltip title="Change order">
        <DragIcon
          className="cursor-move"
          ref={setActivatorNodeRef}
          {...listeners}
        />
      </CommonTooltip>
    </CanPerform>
  );
};

const RegisterFieldsTableList = React.memo(
  forwardRef(
    (
      {
        tableHeaders,
        setTableHeaders,
        tableColumns,
        setTableColumns,
        isEditControl,
        isEditable,
        isDisabled,
        isProjectAdminOrInspector,
        showDetailPrompt,
        showTableFieldsPrompt,
        handleTableRouterPromptUpdate,
        searchValue,
        setSearchValue,
        selectedAggregates,
        setSelectedAggregates,
        onTableDirty,
        onTableSave,
        onTableErrorCheck,
        handleRegisterLogDetailRefetch,
        handleSaveAsDraft,
        showTableFieldConfirmModal,
        setShowTableFieldConfirmModal,
        historyData,
        registerId,
        showAll,
        showDeletedEntries,
        handleHisotryRefetch,
        registerLog,
      },
      ref,
    ) => {
      const {
        location,
        params: { projectId, registerLogId },
      } = useRouter();
      const { dispatch, getCurrentUserRole, getCurrentProjectDetail, state } =
        useContext(AppContext);
      const currentProjectDetails = getCurrentProjectDetail();
      const initialPagination = {
        skip: 0,
        limit: DEFAULT_PAGE_SIZE,
        search: searchValue || null,
        registerLogHistoryId: historyData?.isLatest
          ? undefined
          : historyData?.id,
        isFromTodo: false,
      };
      const [registerEntryFilter, setRegisterEntryFilter] =
        useState(initialPagination);

      const [form] = Form.useForm();
      const [numericFields, setNumericFields] = useState([]);
      const [showModal, setShowModal] = useState(false);
      const [showDeleteModal, setShowDeleteModal] = useState(false);
      const [tableData, setTableData] = useState([]);
      const [totalTableDataCount, setTotalTableDataCount] = useState(0);
      const fieldsData = useRef([]);
      const modifiedFieldsRef = useRef(new Set());
      const isReindexingRef = useRef(false);
      const [isLoading, setIsLoading] = useState(false);
      const [scrollFlag, setScrollFlag] = useState(false);
      const [hasMore, setHasMore] = useState(true);
      const [selectedField, setSelectedField] = useState(false);
      const [showDeleteRecordModal, setShowDeleteRecordModal] = useState(false);
      const [selectedRecordToDelete, setSelectedRecordToDelete] = useState();
      const [isOptionsFocused, setIsOptionsFocused] = useState(null);
      const [isMultiSelectOptionsFocused, setIsMultiSelectOptionsFocused] =
        useState(null);
      const [isDateFocused, setIsDateFocused] = useState(null);
      const [isTimeFocused, setIsTimeFocused] = useState(null);
      const [isFieldClicked, setIsFieldClicked] = useState(null);
      const [editingKeys, setEditingKeys] = useState(new Set());
      const imageKeys = useRef([]);
      const tempUploadDataRef = useRef();
      const [deletingFile, setDeletingFile] = useState(false);
      const fileListRef = useRef({});
      const [uploadKey, setUploadKey] = useState(0);
      const [isCreatingEntry, setIsCreatingEntry] = useState(false);
      const [rowRecord, setRowRecord] = useState();
      const [fieldIndex, setFieldIndex] = useState();
      const [showFillModal, setShowFillModal] = useState(false);
      const [isFormChanged, setIsFormChanged] = useState(false);
      const [aggregateResults, setAggregateResults] = useState({});
      const [showCommentModal, setShowCommentModal] = useState(false);
      const [selectedCommentRecord, setSelectedCommentRecord] = useState(null);
      const [showAdditionalCommentsModal, setShowAdditionalCommentsModal] =
        useState(false);
      const [selectedAdditionalComments, setSelectedAdditionalComments] =
        useState([]);
      // Track locally created or updated row IDs for badge indicator
      const [locallyModifiedRowIds, setLocallyModifiedRowIds] = useState(
        new Set(),
      );
      const isDesktopViewport = useMedia(
        `(min-width: ${BREAKPOINTS.desktop}px)`,
      );
      const isMobileViewport = useMedia(`(max-width: ${BREAKPOINTS.mobile}px)`);
      const { handleAggregateChange, handleSelectAll, handleClearAll } =
        createAggregateHandlers(
          selectedAggregates,
          setSelectedAggregates,
          setAggregateResults,
        );
      const createEntryTimeoutRef = useRef(null);
      const [registerStatsFilter, setRegisterStatsFilter] = useState({
        registerLogId,
      });
      const tableRef = useRef();
      const focusedFieldKeyRef = useRef(null);
      const isAuditor =
        getCurrentUserRole() === USER_ROLES.EXTERNAL_AUDITOR ||
        state?.projectRole === PROJECT_ROLES.AUDITOR;

      const showRouterPrompt = (value) => {
        dispatch({
          type: 'SET_SHOW_PROMPT',
          data: value,
        });
      };

      // Helper function to check if a row was updated after history entry creation
      const isRowUpdatedAfterHistory = useCallback(
        (rowUpdatedAt, rowId) => {
          if (!historyData?.isLatest) return false;
          if (
            !includes(
              [REGISTER_LOG_STATUS?.REDO, REGISTER_LOG_STATUS?.REDO_DRAFT],
              registerLog?.status,
            )
          )
            return false;

          // Check if row is locally modified (created or updated without refetch)
          if (rowId && locallyModifiedRowIds?.has(rowId?.toString())) {
            return true;
          }

          // Check server-provided updatedAt timestamp
          if (!historyData?.createdAt || !rowUpdatedAt) return false;

          const historyCreatedAtMoment = moment(historyData?.createdAt);
          const rowUpdatedAtMoment = moment(rowUpdatedAt);

          return rowUpdatedAtMoment?.isAfter(historyCreatedAtMoment);
        },
        [historyData, registerLog, locallyModifiedRowIds],
      );

      const onSearchChange = (value) => {
        setSearchValue(value);
      };

      const [getRegisterStats] = useLazyQueryWithCallbacks(REGISTER_STATS, {
        fetchPolicy: 'network-only',
        onCompleted(res) {
          processRegisterStatsResults(res, setAggregateResults);
        },
        onError() {},
      });

      useImperativeHandle(ref, () => ({
        getChangedTableData: () => {
          const currentFormValues = form?.getFieldsValue();
          const processedTableData = fieldsData?.current || [];
          const tableFieldEntries = [];
          const fileListRefData = fileListRef?.current || {};
          processedTableData?.forEach((row, rowIndex) => {
            const isNewRow = !row?.id;
            const rowHasChanges = tableHeaders?.some((col) =>
              modifiedFieldsRef?.current?.has(`${rowIndex}_${col?.dataIndex}`),
            );
            if (!rowHasChanges) return;
            const registerFormFieldEntryId = row?.id;
            const rowNumber = rowIndex + 1;
            // Filter out deleted fields before processing
            const activeHeaders = tableHeaders?.filter(
              (header) => !header?.deletedAt,
            );
            activeHeaders?.forEach((column) => {
              const dataIndex = column?.dataIndex;
              const fieldName = `${rowIndex}_${dataIndex}`;
              const registerFormFieldId = column?.id;
              if (registerFormFieldId) {
                let value = row?.[dataIndex];
                if (value === undefined) {
                  value = currentFormValues?.[fieldName];
                }
                if (column?.type === FIELD_TYPES?.ATTACHMENT) {
                  const currentFiles = fileListRefData?.[fieldName] || [];
                  const fileKeys = currentFiles
                    ?.filter((item) => item?.key)
                    ?.map((item) => item?.key);
                  value = fileKeys?.slice(0, 3)?.join(',') || '';
                } else if (
                  column?.type === FIELD_TYPES?.CHECKBOXES &&
                  Array?.isArray(value)
                ) {
                  value = value?.join(',') || [];
                } else if (
                  column?.type === FIELD_TYPES?.DATE &&
                  moment?.isMoment(value)
                ) {
                  value = value?.format(DEFAULTDATEFORMAT);
                } else if (
                  column?.type === FIELD_TYPES?.TIME &&
                  moment?.isMoment(value)
                ) {
                  value = value?.format(TIME_24_HOUR_FORMAT);
                }
                let processedValue =
                  value === null || value === undefined
                    ? ''
                    : String(value)?.trim();
                if (processedValue === '-') {
                  processedValue = '';
                }
                tableFieldEntries?.push({
                  registerFormFieldId,
                  value: processedValue,
                  registerFormFieldEntryId: isNewRow
                    ? null
                    : registerFormFieldEntryId,
                  rowNumber,
                });
              }
            });
          });
          return tableFieldEntries;
        },
        clearFormErrors: () => {
          const fields = form?.getFieldsError()?.map(({ name }) => ({
            name,
            errors: [],
          }));
          form?.setFields(fields);
        },
        setTableData: () => {
          setTableData(fieldsData?.current);
        },
        cancelPendingSave: () => {
          if (createEntryTimeoutRef?.current) {
            clearTimeout(createEntryTimeoutRef?.current);
          }
        },
        resetFormChanged: () => {
          setIsFormChanged(false);
        },
        resetModifiedFields: () => {
          modifiedFieldsRef?.current?.clear();
          setIsFormChanged(false);
        },
        updateTableIds: (savedEntries) => {
          if (
            !savedEntries ||
            !Array?.isArray(savedEntries) ||
            savedEntries?.length === 0
          )
            return;
          const currentData = [...fieldsData?.current];
          const newEditingKeys = new Set(editingKeys);
          let hasChanges = false;
          const newRowIndices = currentData
            ?.map((row, index) => ({ hasId: !!row?.id, originalIndex: index }))
            ?.filter((item) => !item?.hasId)
            ?.map((item) => item?.originalIndex);
          const newlyCreatedIds = [];
          savedEntries?.forEach((entry, i) => {
            const tableRowIndex = newRowIndices?.[i];
            if (tableRowIndex !== undefined) {
              const row = currentData?.[tableRowIndex];
              const newId = entry?.id;
              const oldKey = row?.key;
              currentData[tableRowIndex] = {
                ...row,
                id: newId,
                key: newId?.toString(),
                isNew: false,
                isEmptyRow: false,
              };
              newlyCreatedIds?.push(newId?.toString());
              if (newEditingKeys?.has(oldKey)) {
                newEditingKeys?.delete(oldKey);
                newEditingKeys?.add(newId?.toString());
              }
              hasChanges = true;
            }
          });
          if (hasChanges) {
            fieldsData.current = currentData;
            setTableData(currentData);
            setEditingKeys(newEditingKeys);
            // Track these newly created row IDs for badge indicator
            if (newlyCreatedIds?.length > 0) {
              setLocallyModifiedRowIds((prev) => {
                const newSet = new Set(prev);
                newlyCreatedIds?.forEach((id) => newSet?.add(id));
                return newSet;
              });
            }
          }
        },
        isTableDirty: () => {
          const modifiedSet = modifiedFieldsRef?.current;
          if (!modifiedSet || modifiedSet?.size === 0) return false;
          const allValues = form?.getFieldsValue();
          let hasBlockingChanges = false;
          const modifiedRowIndices = new Set();
          modifiedSet?.forEach((key) => {
            const [rowIndex] = key?.split('_');
            modifiedRowIndices?.add(rowIndex);
          });
          modifiedRowIndices?.forEach((rowIndex) => {
            if (hasBlockingChanges) return;
            const isRowComplete = filter(
              tableHeaders,
              (item) => !item?.deletedAt,
            )?.every((header) => {
              if (!header?.isRequired) return true;
              const fieldName = `${rowIndex}_${header?.dataIndex}`;
              const val = allValues?.[fieldName];
              return (
                val !== undefined &&
                val !== null &&
                val !== '' &&
                val !== '-' &&
                (!Array?.isArray(val) || val?.length > 0)
              );
            });
            if (!isRowComplete) {
              const hasAnyData = tableHeaders?.some((header) => {
                const fieldName = `${rowIndex}_${header?.dataIndex}`;
                const val = allValues?.[fieldName];
                return (
                  val !== undefined &&
                  val !== null &&
                  val !== '' &&
                  val !== '-' &&
                  (!Array?.isArray(val) || val?.length > 0)
                );
              });
              if (hasAnyData) {
                hasBlockingChanges = true;
              }
            }
          });
          return hasBlockingChanges;
        },
      }));

      const refetchAggregateStats = useCallback(() => {
        if (Object?.keys(selectedAggregates)?.length > 0) {
          const aggregateData = Object?.entries(selectedAggregates)?.map(
            ([fieldId, operations]) => ({
              id: fieldId,
              operations,
            }),
          );

          getRegisterStats({
            variables: {
              data: {
                fields: aggregateData,
                registerId,
                type: REGISTER_DETAILS_TYPES.FORM_FIELD,
              },
              filter: registerStatsFilter,
            },
          });
        }
      }, [
        selectedAggregates,
        registerId,
        registerStatsFilter,
        getRegisterStats,
      ]);

      const [getRegisterLogFormFieldsEntryList, { loading }] = useLazyQueryWithCallbacks(
        GET_REGISTER_LOG_FORM_FIELDS_ENTRY_LIST,
        {
          fetchPolicy: 'network-only',
          onError() {},
          onCompleted(response) {
            const total = response?.registerLogFormFieldEntryList?.total;
            setTotalTableDataCount(total);
            const data = response?.registerLogFormFieldEntryList?.data;
            fileListRef.current = {};
            setUploadKey((prev) => prev + 1);
            // Clear locally modified row IDs when fresh data is fetched
            if (!scrollFlag) {
              setLocallyModifiedRowIds(new Set());
            }
            const transformedData =
              data?.map((item) => {
                const transformedItem = {
                  key: item?.id?.toString(),
                  id: item?.id || '',
                  comments: item?.comments || [],
                  updatedAt: item?.updatedAt,
                  ...Object.entries(item?.responses || {})?.reduce(
                    (acc, [key, value]) => {
                      const field = tableColumns?.find(
                        (col) => col?.dataIndex === toCamelCase(key),
                      );
                      if (field?.type === FIELD_TYPES.DATE) {
                        if (value) {
                          acc[toCamelCase(key)] = moment(
                            value,
                            DEFAULTDATEFORMAT,
                          )?.isValid()
                            ? moment(value, DEFAULTDATEFORMAT)
                            : null;
                        } else {
                          acc[toCamelCase(key)] = null;
                        }
                      } else if (field?.type === FIELD_TYPES.TIME) {
                        if (value) {
                          acc[toCamelCase(key)] = moment(
                            value,
                            TIME_24_HOUR_FORMAT,
                            true,
                          )?.isValid()
                            ? moment(value, TIME_12_HOUR_FORMAT)
                            : null;
                        } else {
                          acc[toCamelCase(key)] = null;
                        }
                      } else if (field?.type === FIELD_TYPES.CHECKBOXES) {
                        const values = value
                          ? value?.split(',')?.filter(Boolean)
                          : [];
                        acc[toCamelCase(key)] =
                          values?.length > 0 ? values : undefined;
                      } else if (field?.type === FIELD_TYPES.OPTIONS) {
                        acc[toCamelCase(key)] = value || undefined;
                      } else {
                        acc[toCamelCase(key)] = value;
                      }
                      return acc;
                    },
                    {},
                  ),
                };
                return transformedItem;
              }) || [];

            const newRow = tableColumns?.reduce(
              (acc, field) => {
                acc[field?.dataIndex] = undefined;
                return acc;
              },
              { key: `new-${Date.now()}`, isEmptyRow: true },
            );

            const reversedData = [...transformedData];
            const hasSearchFilter = registerEntryFilter?.search;
            if (scrollFlag) {
              const dataList = [...tableData, ...reversedData];
              if (
                !isDisabled &&
                !isAuditor &&
                dataList?.length === total &&
                !hasSearchFilter
              ) {
                reversedData?.push(newRow);
                dataList?.push(newRow);
              }
              fieldsData.current = dataList;
              if (!hasSearchFilter) {
                setEditingKeys((prev) => new Set([...prev, newRow?.key]));
              }
              setTableData(dataList);
              setScrollFlag(false);
            } else {
              if (
                !isDisabled &&
                !isAuditor &&
                reversedData?.length === total &&
                !hasSearchFilter
              ) {
                reversedData?.push(newRow);
              }
              fieldsData.current = reversedData;
              setEditingKeys(
                reversedData?.length > 0 &&
                  reversedData?.[reversedData?.length - 1]?.isEmptyRow
                  ? new Set([reversedData[reversedData?.length - 1]?.key])
                  : new Set(),
              );
              setTableData(reversedData);
            }
            setHasMore(!!data?.length);
            if (total > 0) {
              refetchAggregateStats();
            } else if (
              total === 0 &&
              Object?.keys(selectedAggregates)?.length > 0
            ) {
              setAggregateResults({});
            }
            setIsLoading(false);
          },
        },
      );

      const [getRegister] = useLazyQueryWithCallbacks(GET_REGISTER, {
        fetchPolicy: 'network-only',
        onError() {},
        onCompleted(response) {
          const data = response?.getRegister?.registerFormFields;
          const filteredColumns = map(data, (column) => ({
            ...column,
            dataIndex: toCamelCase(column?.name),
            index: toCamelCase(column?.name),
          }));
          setTableHeaders(filteredColumns);
        },
      });

      const handleRegisterRefetch = () => {
        getRegister({ variables: { getRegisterId: registerId } });
      };

      const renderAggregateDropdown = (
        key,
        fieldType,
        calculationResultType,
      ) => (
        <AggregateDropdown
          fieldKey={key}
          fieldType={fieldType}
          calculationResultType={calculationResultType}
          selectedAggregates={selectedAggregates}
          onAggregateChange={(fieldKey, checkedValues) =>
            handleAggregateChange(
              fieldKey,
              checkedValues,
              getRegisterStats,
              registerId,
              REGISTER_DETAILS_TYPES.FORM_FIELD,
              registerStatsFilter,
            )
          }
          onSelectAll={(fieldKey) =>
            handleSelectAll(
              fieldKey,
              getRegisterStats,
              registerId,
              REGISTER_DETAILS_TYPES.FORM_FIELD,
              registerStatsFilter,
            )
          }
          onClearAll={(fieldKey) =>
            handleClearAll(
              fieldKey,
              getRegisterStats,
              registerId,
              REGISTER_DETAILS_TYPES.FORM_FIELD,
              registerStatsFilter,
            )
          }
          hasData={totalTableDataCount > 0}
        />
      );

      const checkAllTableErrors = () => {
        const errors = form?.getFieldsError();
        const hasFormErrors = errors?.some(
          (field) => field?.errors?.length > 0,
        );
        if (hasFormErrors) {
          onTableErrorCheck?.(true);
          return true;
        }
        const allValues = form?.getFieldsValue();
        const hasPartialRowErrors = fieldsData?.current?.some(
          (row, rowIndex) => {
            if (row?.isEmptyRow) {
              return false;
            }
            const hasDataInRow = tableHeaders?.some((field) => {
              const fieldKey = `${rowIndex}_${field?.dataIndex}`;
              const fieldValue = allValues?.[fieldKey];
              const isEmpty =
                !fieldValue ||
                fieldValue === '' ||
                fieldValue === '-' ||
                (Array.isArray(fieldValue) && fieldValue?.length === 0);
              return !isEmpty;
            });
            const hasMissingMandatoryInRow = tableHeaders?.some((field) => {
              if (!field?.isRequired) return false;
              const fieldKey = `${rowIndex}_${field?.dataIndex}`;
              const fieldValue = allValues?.[fieldKey];
              const isEmpty =
                !fieldValue ||
                fieldValue === '' ||
                fieldValue === '-' ||
                (Array?.isArray(fieldValue) && fieldValue?.length === 0);
              return isEmpty;
            });
            return hasDataInRow && hasMissingMandatoryInRow;
          },
        );
        onTableErrorCheck?.(hasPartialRowErrors);
        return hasPartialRowErrors;
      };

      const clearFocus = () => {
        setIsDateFocused(null);
        setIsTimeFocused(null);
        setIsOptionsFocused(null);
        setIsMultiSelectOptionsFocused(null);
      };

      const [createRegisterLogFormFieldEntry] = useMutation(
        CREATE_REGISTER_TABLE_FIELDS,
        {
          fetchPolicy: 'network-only',
          onError() {},
          onCompleted() {
            handleTableRouterPromptUpdate(false);
            showRouterPrompt(
              showDetailPrompt?.current || showTableFieldsPrompt?.current,
            );
            refetchAggregateStats();
            checkAllTableErrors();
            const hasRemainingErrors = checkAllTableErrors();
            if (onTableSave) {
              onTableSave(hasRemainingErrors);
            }
            handleRegisterRefetch();
            handleRegisterLogDetailRefetch?.();
            handleHisotryRefetch?.();
          },
        },
      );

      const [updateRegisterFormFieldSequences] = useMutation(
        UPDATE_REGISTER_FORM_FIELD_SEQUENCES,
        {
          onError() {},
          onCompleted: () => {
            if (
              location?.pathname !==
              `${ROUTES.PROJECTS}/${projectId}/${TAB_KEYS.REGISTER}/${registerId}/edit-control`
            ) {
              getRegisterLogFormFieldsEntryList({
                variables: { registerLogId, filter: initialPagination },
              });
            }
            handleRegisterRefetch();
          },
        },
      );

      const [updateRegisterLogFormFieldEntry] = useMutation(
        UPDATE_REGISTER_LOG_DETAIL_ENTRY,
        {
          fetchPolicy: 'network-only',
          onError() {},
          onCompleted() {
            handleTableRouterPromptUpdate(false);
            showRouterPrompt(
              showDetailPrompt?.current || showTableFieldsPrompt?.current,
            );
            refetchAggregateStats();
            checkAllTableErrors();
            const hasRemainingErrors = checkAllTableErrors();
            if (onTableSave) {
              onTableSave(hasRemainingErrors);
            }
            handleRegisterRefetch();
            handleRegisterLogDetailRefetch?.();
            handleHisotryRefetch?.();
          },
        },
      );

      const [fetchSignedUrl] = useLazyQueryWithCallbacks(GET_URLS, {
        fetchPolicy: 'network-only',
        onCompleted: async (response) => {
          const promises = map(
            response?.getSignedPutUrls?.signedUrls,
            async (imageUrl, index) => {
              const objectFiles = tempUploadDataRef?.current?.filter(
                (item) => item?.originFileObj,
              );
              const callBack = async (image) => {
                const watermarkedImageUrl = await addWatermark(image);
                const file = dataURLtoFile(
                  watermarkedImageUrl,
                  objectFiles?.[index]?.originFileObj?.name,
                );
                await fileUpload(imageUrl, file)?.catch(() => {
                  return message.error('Image upload failed!');
                });
              };
              if (
                includes(
                  [...ALLOWED_IMAGE_TYPES],
                  objectFiles?.[index]?.originFileObj?.type,
                )
              ) {
                await compressImage({
                  file: objectFiles?.[index]?.originFileObj,
                  fileType: objectFiles?.[index]?.type?.split('/')?.[1],
                  callBack,
                });
              } else {
                await fileUpload(
                  imageUrl,
                  objectFiles[index]?.originFileObj,
                )?.catch(() => {
                  return message.error('File upload failed!');
                });
              }
            },
          );
          await Promise.all(promises);
        },
        onError() {
          message.error('Error getting upload URL');
        },
      });

      const mouseSensor = useSensor(MouseSensor);
      const touchSensor = useSensor(TouchSensor);
      const keyboardSensor = useSensor(KeyboardSensor);
      const sensors = useSensors(mouseSensor, touchSensor, keyboardSensor);

      useEffect(() => {
        return () => {
          form.resetFields();
        };
      }, []);

      useEffect(() => {
        setRegisterEntryFilter((prevFilter) => ({
          ...prevFilter,
          search: searchValue || null,
          registerLogHistoryId: historyData?.isLatest
            ? undefined
            : historyData?.id,
          skip: 0,
          showAll,
        }));
        setRegisterStatsFilter((prevFilter) => ({
          ...prevFilter,
          search: searchValue || null,
          registerLogHistoryId: historyData?.isLatest
            ? undefined
            : historyData?.id,
        }));
        setScrollFlag(false);
      }, [searchValue, historyData?.id, showAll]);

      // Reset form when filter changes to prevent stale cached values
      useEffect(() => {
        form.resetFields();
        setEditingKeys(new Set());
        modifiedFieldsRef.current = new Set();
        setLocallyModifiedRowIds(new Set());
      }, [showAll, searchValue, historyData?.id]);

      useEffect(() => {
        if (
          location?.pathname !==
            `${ROUTES.PROJECTS}/${projectId}/${TAB_KEYS.REGISTER}/${registerId}/edit-control` &&
          registerId
        ) {
          getRegister({ variables: { getRegisterId: registerId } });
        }
      }, [registerId]);

      useEffect(() => {
        if (
          location?.pathname !==
            `${ROUTES.PROJECTS}/${projectId}/${TAB_KEYS.REGISTER}/${registerId}/edit-control` &&
          registerLog?.id
        ) {
          setIsLoading(true);
          setScrollFlag(false);
          getRegisterLogFormFieldsEntryList({
            variables: { registerLogId, filter: registerEntryFilter },
          });
        }
      }, [registerLogId, registerEntryFilter, registerId, registerLog]);

      useEffect(() => {
        if (
          tableColumns?.length > 0 &&
          location?.pathname ===
            `${ROUTES.PROJECTS}/${projectId}/${TAB_KEYS.REGISTER}/${registerId}/edit-control`
        ) {
          const filteredColumns = map(tableColumns, (column) => ({
            ...column,
            dataIndex: toCamelCase(column?.name),
            index: toCamelCase(column?.name),
          }));
          setTableHeaders(filteredColumns);
        }
      }, [tableColumns]);

      const filterEditingKeys = (
        prevKeys,
        currentFieldsData,
        currentTableHeaders,
        currentForm,
      ) => {
        return new Set(
          [...prevKeys]?.filter((key) => {
            const row = currentFieldsData?.current?.find((r) => r?.key === key);
            if (!row) return false;

            if (row?.isEmptyRow || row?.isNew || row?.isUpdate) {
              return true;
            }

            const rowIndex = currentFieldsData?.current?.findIndex(
              (r) => r?.key === key,
            );
            if (rowIndex !== -1) {
              const rowFieldKeys = currentTableHeaders?.map(
                (header) => `${rowIndex}_${header?.dataIndex}`,
              );
              const hasValidationErrors = rowFieldKeys?.some((fieldKey) => {
                const fieldError = currentForm?.getFieldError(fieldKey);
                return fieldError?.length > 0;
              });

              if (hasValidationErrors) {
                return true;
              }
            }

            return false;
          }),
        );
      };

      useEffect(() => {
        const handleClickOutside = (event) => {
          // eslint-disable-next-line no-undef
          const dropdowns = document.querySelectorAll(
            '.ant-select-dropdown, .ant-picker-dropdown, .ant-dropdown',
          );

          const isInsideDropdown = Array?.from(dropdowns)?.some((dropdown) =>
            dropdown?.contains(event?.target),
          );

          const isFieldControl = event?.target?.closest(
            '.ant-select-clear, .ant-picker-clear, .ant-select-selection-item-remove, .anticon',
          );

          if (isInsideDropdown || isFieldControl) return;

          if (
            tableRef?.current &&
            !tableRef?.current?.contains(event?.target)
          ) {
            clearFocus();
            setIsMultiSelectOptionsFocused(null);
            if (!isFormChanged) return;
            const currentKeys = Array?.from(editingKeys);
            const allValues = form?.getFieldsValue();
            currentKeys?.forEach((key) => {
              const rowIndex = fieldsData?.current?.findIndex(
                (row) => row?.key === key,
              );
              if (rowIndex === -1) return;
              const record = fieldsData?.current?.[rowIndex];
              const rowFieldKeys = Object?.keys(allValues)?.filter((k) =>
                k?.startsWith(`${rowIndex}`),
              );
              const isRowEmpty = rowFieldKeys?.every((k) => {
                const val = allValues?.[k];
                return (
                  includes([...INVALID_FIELD_VALUES, '-'], val) ||
                  (Array?.isArray(val) && val?.length === 0)
                );
              });
              if (isRowEmpty && !record?.id) {
                const clearedFields = rowFieldKeys?.map((k) => ({
                  name: k,
                  errors: [],
                }));
                form?.setFields(clearedFields);
                return;
              }
              const invalidFields = [];
              // Filter out deleted fields before validation
              const activeHeaders = tableHeaders?.filter(
                (header) => !header?.deletedAt,
              );
              activeHeaders?.forEach((field) => {
                if (field?.isRequired) {
                  const fieldName = `${rowIndex}_${field?.dataIndex}`;
                  const fieldValue = allValues?.[fieldName];
                  const isEmpty =
                    !fieldValue ||
                    fieldValue === '' ||
                    fieldValue === '-' ||
                    (Array?.isArray(fieldValue) && fieldValue?.length === 0);
                  if (isEmpty) {
                    invalidFields?.push({
                      name: fieldName,
                      errors: ['This field is required'],
                    });
                  }
                }
              });
              const [focusedRowIndex] =
                focusedFieldKeyRef?.current?.split('_') || [];
              if (String(rowIndex) === String(focusedRowIndex)) {
                if (invalidFields?.length > 0) {
                  form.setFields(invalidFields);
                } else {
                  const validFields = rowFieldKeys?.map((name) => ({
                    name,
                    errors: [],
                  }));
                  form?.setFields(validFields);
                }
              }
            });
            setEditingKeys((prevKeys) =>
              filterEditingKeys(prevKeys, fieldsData, tableHeaders, form),
            );
          }
        };

        // eslint-disable-next-line no-undef
        document.addEventListener('mouseup', handleClickOutside);
        return () => {
          // eslint-disable-next-line no-undef
          document.removeEventListener('mouseup', handleClickOutside);
        };
      }, [tableHeaders, form, editingKeys, isFormChanged]);

      const handleFieldChange = useCallback(
        (changedFields) => {
          const [fieldName, value] = Object?.entries(changedFields)?.[0];
          modifiedFieldsRef?.current?.add(fieldName);
          const [rowIndex, fieldType] = fieldName?.split('_');
          focusedFieldKeyRef.current = fieldName;
          const changedField = tableHeaders?.find(
            (field) => field?.dataIndex === fieldType,
          );
          let formattedValue = value;
          if (changedField?.type === FIELD_TYPES.NUMERIC) {
            formattedValue = formatNumericInput(value);
          }
          form?.setFieldValue(fieldName, formattedValue);
          if (
            changedField?.type === FIELD_TYPES.NUMERIC ||
            changedField?.type === FIELD_TYPES.DATE ||
            changedField?.type === FIELD_TYPES.TIME
          ) {
            const calculationFields = tableHeaders
              ?.filter((field) => field?.type === FIELD_TYPES.CALCULATION)
              ?.map((field) => field?.dataIndex);

            if (calculationFields?.length) {
              calculationFields?.forEach((calcField) => {
                const calcFieldName = `${rowIndex}_${calcField}`;
                const calculatedValue = calculateFieldValue(
                  calcFieldName,
                  form,
                  tableHeaders,
                );
                form?.setFieldValue(calcFieldName, calculatedValue);
                const isRequiredCalcField = tableHeaders?.find(
                  (field) =>
                    field?.dataIndex === calcField && field?.isRequired,
                );
                if (
                  isRequiredCalcField &&
                  includes([...INVALID_FIELD_VALUES, '-'], calculatedValue)
                ) {
                  form?.setFields([
                    {
                      name: calcFieldName,
                      errors: ['This field is required'],
                    },
                  ]);
                } else {
                  form?.setFields([
                    {
                      name: calcFieldName,
                      errors: [],
                    },
                  ]);
                }
                if (fieldsData?.current?.[parseInt(rowIndex, 10)]) {
                  fieldsData.current[parseInt(rowIndex, 10)][calcField] =
                    calculatedValue;
                }
              });
            }
          }

          const updatedData = [...fieldsData?.current];
          const rowIndexNum = parseInt(rowIndex, 10);
          if (updatedData?.[rowIndexNum]) {
            updatedData[rowIndexNum] = {
              ...updatedData[rowIndexNum],
              [fieldType]: formattedValue,
            };

            const isLastRow = rowIndexNum === updatedData?.length - 1;
            const lastRow = updatedData[updatedData?.length - 1];
            if (
              isLastRow &&
              lastRow?.isEmptyRow &&
              formattedValue !== null &&
              formattedValue !== ''
            ) {
              // Remove isEmptyRow flag from current row since it now has data
              delete updatedData[rowIndexNum]?.isEmptyRow;
              const newRow = tableHeaders?.reduce(
                (acc, field) => {
                  acc[field?.dataIndex] = null;
                  return acc;
                },
                { key: `new-${Date.now()}`, isEmptyRow: true },
              );
              updatedData?.push(newRow);
              setEditingKeys((prev) => new Set([...prev, newRow?.key]));
            }
            setTableData(updatedData);
            fieldsData.current = updatedData;
          }
          const allRowValues = form?.getFieldsValue();
          // Filter out deleted fields before validation
          const activeHeaders = tableHeaders?.filter(
            (header) => !header?.deletedAt,
          );

          // Check if the entire row is now empty after this change
          const isRowCompletelyEmpty = activeHeaders?.every((header) => {
            const checkFieldName = `${rowIndex}_${header?.dataIndex}`;
            const checkValue = allRowValues?.[checkFieldName];
            return (
              !checkValue ||
              checkValue === '' ||
              checkValue === '-' ||
              checkValue === null ||
              checkValue === undefined ||
              (Array?.isArray(checkValue) && checkValue?.length === 0)
            );
          });

          // Only delete new rows (without database ID)
          const currentRow = updatedData?.[rowIndexNum];
          const isNewRow = !currentRow?.id;
          const isNotLastEmptyRow = !currentRow?.isEmptyRow;
          const shouldDeleteRow =
            isRowCompletelyEmpty && isNewRow && isNotLastEmptyRow;

          // Only run validation if we're NOT about to delete the row
          if (!shouldDeleteRow) {
            const rowValidationFields = [];
            activeHeaders?.forEach((header) => {
              const checkFieldName = `${rowIndex}_${header?.dataIndex}`;
              const checkValue = allRowValues?.[checkFieldName];
              if (header?.isRequired) {
                const isEmpty =
                  !checkValue ||
                  checkValue === '' ||
                  checkValue === '-' ||
                  (Array?.isArray(checkValue) && checkValue?.length === 0);
                if (isEmpty) {
                  rowValidationFields?.push({
                    name: checkFieldName,
                    errors: ['This field is required'],
                  });
                } else {
                  rowValidationFields?.push({
                    name: checkFieldName,
                    errors: [],
                  });
                }
              }
            });
            if (rowValidationFields?.length > 0) {
              setTimeout(() => {
                form?.setFields(rowValidationFields);
              }, 300);
            }
          }

          // If row is completely empty and it's a new row (not the last empty row), delete it
          if (shouldDeleteRow) {
            const rowPrefix = `${rowIndex}_`;

            // Clear modified fields for this row
            modifiedFieldsRef?.current?.forEach((fieldKey) => {
              if (fieldKey?.startsWith(rowPrefix)) {
                modifiedFieldsRef?.current?.delete(fieldKey);
              }
            });

            // Get all form fields BEFORE deletion
            const allFormFieldsBefore = form?.getFieldsValue();

            // Remove the row from table data
            const currentTableData = [...fieldsData?.current];
            const newTableData = currentTableData?.filter(
              (_, idx) => idx !== rowIndexNum,
            );
            setTableData(newTableData);
            fieldsData.current = newTableData;

            // Remove from editing keys
            setEditingKeys((prev) => {
              const newKeys = new Set(prev);
              newKeys?.delete(currentRow?.key);
              return newKeys;
            });

            // Re-index all form fields after deletion
            const newFormFields = {};
            const clearErrorFields = [];

            Object?.keys(allFormFieldsBefore)?.forEach((oldFieldKey) => {
              const [oldRowIdx, dataIndex] = oldFieldKey?.split('_');
              const oldRowIdxNum = parseInt(oldRowIdx, 10);

              // Skip the deleted row
              if (oldRowIdxNum === rowIndexNum) {
                clearErrorFields?.push({ name: oldFieldKey, errors: [] });
                return;
              }

              // Shift down rows after the deleted one
              if (oldRowIdxNum > rowIndexNum) {
                const newRowIdx = oldRowIdxNum - 1;
                const newFieldKey = `${newRowIdx}_${dataIndex}`;
                newFormFields[newFieldKey] = allFormFieldsBefore[oldFieldKey];
                newFormFields[oldFieldKey] = undefined;
                clearErrorFields?.push({ name: oldFieldKey, errors: [] });
              } else {
                // Keep rows before the deleted one
                newFormFields[oldFieldKey] = allFormFieldsBefore[oldFieldKey];
              }
            });

            // Check immediately if there are any modified fields left
            const hasModifiedFields = modifiedFieldsRef?.current?.size > 0;

            // If no modified fields remain, disable navigation prompt IMMEDIATELY
            if (!hasModifiedFields) {
              setIsFormChanged(false);
              showRouterPrompt(false);
              handleTableRouterPromptUpdate(false);
              if (showTableFieldsPrompt?.current !== undefined) {
                // eslint-disable-next-line no-param-reassign
                showTableFieldsPrompt.current = false;
              }
            }

            // Temporarily disable form change tracking during re-indexing
            const previousFormChangedState = isFormChanged && hasModifiedFields;
            setIsFormChanged(false);

            // Set flag to prevent onFieldsChange from triggering
            isReindexingRef.current = true;

            form?.setFieldsValue(newFormFields);
            form?.setFields(clearErrorFields);

            // Reset flag after re-indexing
            isReindexingRef.current = false;

            // After deletion, update error state and parent isDirty state
            setTimeout(() => {
              if (onTableErrorCheck) {
                // Check if any errors remain after deletion
                const remainingErrors = form?.getFieldsError();
                const hasErrors = remainingErrors?.some(
                  (field) => field?.errors?.length > 0,
                );
                onTableErrorCheck(hasErrors);
              }

              // Update parent's isDirty state by calling onTableSave
              if (onTableSave) {
                const remainingErrors = form?.getFieldsError();
                const hasErrors = remainingErrors?.some(
                  (field) => field?.errors?.length > 0,
                );
                onTableSave(hasErrors);
              }

              // Restore form changed state only if there are still modified fields
              if (hasModifiedFields) {
                setIsFormChanged(previousFormChangedState);
              }
            }, 100);
          }
        },
        [
          tableHeaders,
          form,
          onTableErrorCheck,
          onTableSave,
          showRouterPrompt,
          handleTableRouterPromptUpdate,
        ],
      );

      const getDateData = (column, value, fieldName) => {
        let fieldValue = value;

        if (fieldValue) {
          if (moment(fieldValue)?.isValid()) {
            fieldValue = moment(fieldValue)?.format(DEFAULTDATEFORMAT);
          } else {
            form.setFields([
              {
                name: fieldName,
                errors: ['Please enter a valid date'],
              },
            ]);
            return 'Error';
          }
        } else if (column?.isRequired) {
          form.setFields([
            {
              name: fieldName,
              errors: ['This field is required'],
            },
          ]);
          return 'Error';
        }
        return fieldValue;
      };

      const getTimeData = (column, value, fieldName) => {
        let fieldValue = value;

        if (fieldValue) {
          if (moment(fieldValue)?.isValid()) {
            fieldValue = moment(new Date(fieldValue))?.format(
              TIME_24_HOUR_FORMAT,
            );
          } else {
            form.setFields([
              {
                name: fieldName,
                errors: ['Please enter a valid time'],
              },
            ]);
            return 'Error';
          }
        } else if (column?.isRequired) {
          form.setFields([
            {
              name: fieldName,
              errors: ['This field is required'],
            },
          ]);
          return 'Error';
        }
        return fieldValue;
      };

      const getCheckBoxesData = (column, value, fieldName) => {
        let fieldValue = value;
        if (column?.type === FIELD_TYPES.CHECKBOXES) {
          if (
            !fieldValue ||
            (Array.isArray(fieldValue) && fieldValue?.length === 0)
          ) {
            return '';
          }
          if (fieldValue) {
            fieldValue = isArray(fieldValue)
              ? fieldValue
                  ?.filter(Boolean)
                  ?.map((item) => String(item || ''))
                  ?.join(',')
                  ?.trim()
              : String(fieldValue);
          }
        } else if (column?.type === FIELD_TYPES.OPTIONS) {
          if (!fieldValue) {
            return '';
          }
        }

        if (column?.type === FIELD_TYPES.DATE) {
          const data = getDateData(column, fieldValue, fieldName);
          if (data === 'Error') {
            return 'Error';
          }
          fieldValue = data;
        }
        if (column?.type === FIELD_TYPES.TIME) {
          const data = getTimeData(column, fieldValue, fieldName);
          if (data === 'Error') {
            return 'Error';
          }
          fieldValue = data;
        }

        if (column?.type === FIELD_TYPES.ATTACHMENT) {
          const [rowIndex, name] = fieldName?.split('_');
          const currentData = fieldsData?.current?.[rowIndex]?.[name];
          let updatedKeys = [];
          if (Array.isArray(currentData)) {
            updatedKeys =
              currentData
                ?.filter((item) => item?.key)
                ?.map((item) => item?.key) || [];
          } else if (typeof currentData === 'string') {
            updatedKeys = currentData?.split(',')?.filter(Boolean);
          }

          fieldValue = [...updatedKeys];
          fieldValue = take(fieldValue, 3)?.join(',');
        }
        return fieldValue;
      };

      const createResponsesForEntry = (index, allValues, record) => {
        const responses = [];
        // Always filter out deleted fields before creating responses for mutations
        const activeHeaders = tableHeaders?.filter(
          (header) => !header?.deletedAt,
        );
        activeHeaders?.forEach((column) => {
          const entryFieldName = `${index}_${column?.dataIndex}`;
          let fieldValue = allValues[entryFieldName];

          if (column?.type === FIELD_TYPES.ATTACHMENT && record?.id) {
            const attachmentData = record[column?.dataIndex];
            if (Array.isArray(attachmentData)) {
              const currentFileList =
                fileListRef?.current[entryFieldName] || [];
              const fileKeys = currentFileList
                ?.filter((item) => item?.key || item?.name || item?.url)
                ?.map((item) => {
                  if (item?.key) return item?.key;
                  if (item?.name) return `register/attachments/${item?.name}`;
                  return item?.url;
                })
                ?.filter(Boolean);

              fieldValue = take([...fileKeys], 3)?.join(',');
              responses?.push({
                registerFormFieldId: column?.id,
                value: fieldValue || '',
              });
              return;
            }
          }

          const data = getCheckBoxesData(column, fieldValue, entryFieldName);
          if (data === 'Error') {
            return 'Error';
          }
          fieldValue = data;

          if (column?.type === FIELD_TYPES.ATTACHMENT) {
            imageKeys.current[entryFieldName] = [];
          }

          if (record?.id) {
            responses?.push({
              registerFormFieldId: column?.id,
              value:
                fieldValue === null || fieldValue === undefined
                  ? ''
                  : String(fieldValue),
            });
          } else if (
            !column?.isRequired ||
            (fieldValue !== undefined &&
              fieldValue !== null &&
              fieldValue !== '')
          ) {
            responses?.push({
              registerFormFieldId: column?.id,
              value: Array.isArray(fieldValue)
                ? fieldValue
                : String(fieldValue || ''),
            });
          }
        });
        return responses;
      };

      const handleBlurSave = useCallback(
        async (rowIndex, record) => {
          // Small delay to ensure form values are updated
          await new Promise((resolve) => {
            setTimeout(resolve, 0);
          });

          const allValues = form?.getFieldsValue();
          const rowFieldKeys = Object.keys(allValues)?.filter((key) =>
            key?.startsWith(`${rowIndex}_`),
          );

          // Capture row-specific values immediately to prevent issues with index changes
          const capturedRowValues = {};
          rowFieldKeys?.forEach((key) => {
            capturedRowValues[key] = allValues[key];
          });

          const isRowEmpty = rowFieldKeys?.every((key) => {
            const val = allValues[key];
            return (
              includes([...INVALID_FIELD_VALUES, '-'], val) ||
              (Array.isArray(val) && val?.length === 0)
            );
          });

          if (isRowEmpty && !record?.id) {
            const clearedFields = rowFieldKeys?.map((key) => ({
              name: key,
              errors: [],
            }));
            form?.setFields(clearedFields);
            handleTableRouterPromptUpdate(false);
            showRouterPrompt(false);
            return;
          }

          try {
            // Filter out deleted fields before validation
            const activeHeaders = tableHeaders?.filter(
              (header) => !header?.deletedAt,
            );
            const activeFieldKeys = rowFieldKeys?.filter((key) => {
              const fieldName = key?.split('_')?.[1];
              return activeHeaders?.some(
                (header) => header?.dataIndex === fieldName,
              );
            });

            await form?.validateFields(activeFieldKeys, { validateOnly: true });

            if (createEntryTimeoutRef?.current) {
              clearTimeout(createEntryTimeoutRef?.current);
            }
            createEntryTimeoutRef.current = setTimeout(async () => {
              // Use captured values to ensure we have the correct field values
              // even if row indices have shifted during re-renders
              // Create a merged values object with captured row values
              const currentFormValues = form?.getFieldsValue();
              const mergedValues = { ...currentFormValues, ...capturedRowValues };
              // Filter out deleted fields before validation
              const nonDeletedHeaders = tableHeaders?.filter(
                (header) => !header?.deletedAt,
              );
              const requiredFields = nonDeletedHeaders?.filter(
                (field) => field?.isRequired,
              );
              const invalidFields = [];
              requiredFields?.forEach((field) => {
                const fieldName = `${rowIndex}_${field?.dataIndex}`;
                const fieldValue = capturedRowValues[fieldName];

                const isEmpty =
                  !fieldValue ||
                  fieldValue === '' ||
                  fieldValue === '-' ||
                  (Array.isArray(fieldValue) && fieldValue?.length === 0);

                if (isEmpty) {
                  invalidFields?.push({
                    name: fieldName,
                    errors: ['This field is required'],
                  });
                }

                if (field?.type === FIELD_TYPES.NUMERIC && fieldValue) {
                  const numValue = Number(fieldValue);
                  if (Number.isNaN(numValue)) {
                    invalidFields?.push({
                      name: fieldName,
                      errors: ['Please enter a valid number'],
                    });
                  }
                }
              });

              if (invalidFields?.length > 0) {
                form.setFields(invalidFields);
                return;
              }

              const responses = createResponsesForEntry(
                rowIndex,
                mergedValues,
                record,
              );
              if (responses?.length > 0) {
                try {
                  if (record?.id) {
                    await updateRegisterLogFormFieldEntry({
                      variables: {
                        updateRegisterLogFormFieldEntryId: record?.id,
                        data: {
                          isNewRow: false,
                          responses,
                        },
                      },
                    });
                    setTableData([...fieldsData?.current]);
                    // Track this updated row for badge indicator
                    setLocallyModifiedRowIds((prev) => {
                      const newSet = new Set(prev);
                      newSet?.add(record?.id?.toString());
                      return newSet;
                    });
                    const rowPrefix = `${rowIndex}_`;
                    modifiedFieldsRef?.current?.forEach((value) => {
                      if (value?.startsWith(rowPrefix)) {
                        modifiedFieldsRef?.current?.delete(value);
                      }
                    });
                  } else if (!isCreatingEntry) {
                    setIsCreatingEntry(true);
                    try {
                      const result = await createRegisterLogFormFieldEntry({
                        variables: {
                          registerLogId,
                          data: {
                            filling: null,
                            tableEntries: responses,
                          },
                        },
                      });

                      if (result?.data?.createRegisterLogFormFieldEntry?.id) {
                        const newId =
                          result?.data?.createRegisterLogFormFieldEntry?.id;
                        const updatedData = [...fieldsData?.current];
                        const trimmedData = Object?.fromEntries(
                          Object?.entries(updatedData[rowIndex])?.map(
                            ([key, value]) => [
                              key,
                              typeof value === 'string' ? value?.trim() : value,
                            ],
                          ),
                        );
                        updatedData[rowIndex] = {
                          ...trimmedData,
                          id: newId,
                          key: newId,
                          isEmptyRow: false,
                        };
                        fieldsData.current = updatedData;
                        setTableData(updatedData);
                        setTotalTableDataCount((prev) => prev + 1);
                        // Track this newly created row for badge indicator
                        setLocallyModifiedRowIds((prev) => {
                          const newSet = new Set(prev);
                          newSet?.add(newId?.toString());
                          return newSet;
                        });
                        const rowPrefix = `${rowIndex}_`;
                        modifiedFieldsRef?.current?.forEach((value) => {
                          if (value?.startsWith(rowPrefix)) {
                            modifiedFieldsRef?.current?.delete(value);
                          }
                        });
                      }
                      if (record?.id) {
                        setEditingKeys((prev) => {
                          const newKeys = new Set(prev);
                          if (!record?.isEmptyRow) newKeys?.delete(record?.key);
                          return newKeys;
                        });
                      }
                    } finally {
                      setIsCreatingEntry(false);
                    }
                  }
                } catch (error) {
                  return error;
                }
              }
            }, 300);
          } catch (error) {
            handleTableRouterPromptUpdate(true);
            showRouterPrompt(true);
            return error;
          }
        },
        [
          tableHeaders,
          form,
          registerLogId,
          createRegisterLogFormFieldEntry,
          updateRegisterLogFormFieldEntry,
          isCreatingEntry,
          setIsCreatingEntry,
          setTableData,
          fieldsData,
          setLocallyModifiedRowIds,
          modifiedFieldsRef,
          setEditingKeys,
          setTotalTableDataCount,
          handleTableRouterPromptUpdate,
          showRouterPrompt,
        ],
      );

      const handleFieldsDataRefUpdate = (value, index, fieldName) => {
        if (index != null && fieldName != null && fieldName !== '') {
          fieldsData.current[index][fieldName] = value;
        } else {
          fieldsData.current = value;
        }
      };

      const handleFileListRefUpdate = (value, key) => {
        if (key) {
          fileListRef.current[key] = value;
        } else {
          fileListRef.current = value;
        }
      };

      const handleUploadImage = async (
        newFileList,
        fieldName,
        record,
        dataIndex,
        index,
      ) => {
        if (createEntryTimeoutRef?.current) {
          clearTimeout(createEntryTimeoutRef?.current);
        }
        const limitedFileList = newFileList?.slice(-3);

        const fileSend = (anotherFileList) => {
          return map(anotherFileList, (file) => {
            const fileType = file?.originFileObj?.type || file?.type;
            const fileName = file?.originFileObj?.name || file?.name;

            return {
              fileName: `register/attachments/${fileName}`,
              contentType: fileType,
            };
          });
        };
        const filteredFileList = filter(
          limitedFileList,
          (file) => file?.originFileObj?.name,
        );

        if (filteredFileList?.length) {
          try {
            fetchSignedUrl({
              variables: {
                data: fileSend(filteredFileList),
              },
            })?.then(({ data }) => {
              handleTableRouterPromptUpdate(true);
              showRouterPrompt(true);
              const keys = data?.getSignedPutUrls?.keys || [];

              if (fileListRef?.current?.[fieldName] && keys?.length > 0) {
                const updatedFileList = [...fileListRef?.current[fieldName]];

                filteredFileList?.forEach((file, i) => {
                  if (keys[i]) {
                    const fileIndex = updatedFileList?.findIndex(
                      (f) => f?.uid === file?.uid,
                    );
                    if (fileIndex >= 0) {
                      updatedFileList[fileIndex] = {
                        ...updatedFileList[fileIndex],
                        key: keys[i],
                      };
                    }
                  }
                });

                fileListRef.current[fieldName] = updatedFileList;

                if (fieldsData?.current?.[index]) {
                  fieldsData.current[index] = {
                    ...fieldsData?.current[index],
                    [dataIndex]: updatedFileList?.map((file) => ({
                      uid: file?.uid,
                      status: file?.status,
                      url: file?.url,
                      value: file?.url,
                      key: file?.key,
                      name: file?.name,
                      thumbUrl: file?.thumbUrl,
                    })),
                  };
                }
              }

              const uploadedKeys = imageKeys?.current?.[fieldName];
              if (uploadedKeys) {
                imageKeys.current[fieldName] = [...uploadedKeys, ...keys];
              } else {
                imageKeys.current[fieldName] = [...keys];
              }
              const key = `${index}_${dataIndex}`;
              form?.setFieldsValue({
                [key]: keys,
              });
              handleBlurSave(index, record);
            });
          } catch (error) {
            return error;
          }
        }
      };

      const handleOnChange = debounce(
        (info, fieldName, record, dataIndex, index) => {
          modifiedFieldsRef?.current?.add(fieldName);
          const newFileList = info?.fileList?.filter((file) => {
            const fileType = file?.originFileObj?.type || file?.type;
            if (
              !file?.url &&
              fileType &&
              !includes([...ALLOWED_IMAGE_TYPES, 'application/pdf'], fileType)
            ) {
              message?.destroy();
              message?.error('File type should be PNG, JPG, JPEG,PDF');
              return false;
            }

            return true;
          });
          if (!isMobileViewport) {
            handleFieldChange({ [fieldName]: newFileList });
          }
          tempUploadDataRef.current = newFileList;
          handleUploadImage(newFileList, fieldName, record, dataIndex, index);
        },
        300,
      );

      const handlePreview = async (file) => {
        let previewUrl = file?.url;
        if (file?.originFileObj) {
          previewUrl = URL.createObjectURL(file?.originFileObj);
        } else if (file?.thumbUrl && !file?.url) {
          previewUrl = file?.thumbUrl;
        }

        if (previewUrl) {
          // eslint-disable-next-line no-undef
          window?.open(previewUrl, '_blank');
          if (file?.originFileObj) {
            URL.revokeObjectURL(previewUrl);
          }
        }
      };

      const handleRowClick = (record, rowIndex, fieldName) => {
        if (isMobileViewport) {
          const updatedRecord = fieldsData?.current[rowIndex];
          setRowRecord(updatedRecord);
          setFieldIndex(rowIndex);
          setShowFillModal(true);
          setIsFieldClicked(fieldName);
        }
      };

      const isEditing = (record) => {
        if (!record) return;
        return editingKeys.has(record.key);
      };

      const handleScroll = (event) => {
        const { target } = event;
        const { scrollTop, scrollHeight, offsetHeight } = target || {};
        const scrolledToBottom =
          (scrollTop + offsetHeight) / scrollHeight >= SCROLL_PERCENT;
        if (scrolledToBottom && hasMore && !loading && !scrollFlag) {
          if (tableData?.length >= totalTableDataCount) return;
          setScrollFlag(true);
          getRegisterLogFormFieldsEntryList({
            variables: {
              registerLogId,
              filter: {
                ...registerEntryFilter,
                registerLogHistoryId: historyData?.isLatest
                  ? undefined
                  : historyData?.id,
                skip: tableData?.length,
              },
            },
          });
        }
      };

      const handleEditField = (field) => {
        setSelectedField(field);
        setShowModal(true);
      };

      const handleDeleteRecord = (record, index) => {
        setFieldIndex(index);
        setSelectedRecordToDelete(record);
        setShowDeleteRecordModal(true);
      };

      const getApproverComment = (text, record) => {
        const hasComment = record?.comments?.length > 0;
        return (
          <CommentBlackIcon
            onClick={(e) => {
              e?.stopPropagation();
              if (hasComment) {
                setSelectedCommentRecord(record?.comments?.[0]);
                setShowCommentModal(true);
              }
            }}
            className={hasComment ? 'has-remark' : 'no-remark'}
          />
        );
      };

      const filteredTableHeaders = useMemo(() => {
        if (!tableHeaders?.length) return [];
        if (showDeletedEntries) return tableHeaders;
        return tableHeaders?.filter((header) => !header?.deletedAt);
      }, [tableHeaders, showDeletedEntries]);

      const columns = useMemo(
        () => [
          ...(!isEditControl
            ? [
                {
                  title: '#',
                  key: 'index',
                  dataIndex: 'index',
                  width: 100,
                  render: (_, record, index) => {
                    const rowNumber = index + 1;
                    const showBadge = isRowUpdatedAfterHistory(
                      record?.updatedAt,
                      record?.id,
                    );
                    return showBadge ? (
                      <Badge dot offset={[5, 0]} color="#1890ff">
                        {rowNumber}
                      </Badge>
                    ) : (
                      rowNumber
                    );
                  },
                },
              ]
            : []),
          ...(filteredTableHeaders?.length > 0
            ? filteredTableHeaders?.map((field) => {
                return {
                  ...field,
                  dataIndex: toCamelCase(field?.name),
                  key: toCamelCase(field?.name),
                  id: field?.id,
                  width: !isEditControl && 230,
                  editable: true,
                  onHeaderCell: (column) => ({
                    column,
                    dataIndex: field?.dataIndex,
                  }),
                  onCell: (record, rowIndex) => ({
                    record,
                    dataIndex: toCamelCase(field?.name),
                    title: field?.name,
                    fieldId: field?.id,
                    fieldType: field?.type,
                    editing: isEditing(record),
                    isRequired: field?.isRequired,
                    options: field?.options || [],
                    index: rowIndex,
                    fieldsData,
                    onFieldClick: () => {
                      if (isMobileViewport) {
                        handleRowClick(
                          record,
                          rowIndex,
                          `${rowIndex}_${toCamelCase(field?.name)}`,
                        );
                      }
                    },
                    isFieldClicked,
                    isMobileViewport,
                    isAuditor,
                    isProjectAdminOrInspector,
                    isOptionsFocused,
                    setIsOptionsFocused,
                    isMultiSelectOptionsFocused,
                    setIsMultiSelectOptionsFocused,
                    isDateFocused,
                    setIsDateFocused,
                    onDateOpenChange: async (e) => {
                      if (!e) {
                        setIsDateFocused(null);
                        // For new rows without ID, call handleBlurSave directly
                        // as it has its own validation logic
                        if (!record?.id) {
                          handleBlurSave(rowIndex, record);
                          return;
                        }
                        try {
                          // Filter out deleted fields before validation
                          const activeHeaders = tableHeaders?.filter(
                            (header) => !header?.deletedAt,
                          );
                          const allValues = form?.getFieldsValue();
                          const rowFieldKeys = Object.keys(allValues)?.filter(
                            (key) => key?.startsWith(`${rowIndex}`),
                          );
                          const activeFieldKeys = rowFieldKeys?.filter(
                            (key) => {
                              const fieldName = key?.split('_')?.[1];
                              return activeHeaders?.some(
                                (header) => header?.dataIndex === fieldName,
                              );
                            },
                          );

                          await form.validateFields(activeFieldKeys, {
                            validateOnly: true,
                          });
                          handleBlurSave(rowIndex, record);
                        } catch (error) {
                          handleTableRouterPromptUpdate(true);
                          showRouterPrompt(true);
                        }
                      }
                    },
                    isTimeFocused,
                    setIsTimeFocused,
                    onTimeOpenChange: async (e) => {
                      if (!e) {
                        setIsTimeFocused(null);
                        // For new rows without ID, call handleBlurSave directly
                        // as it has its own validation logic
                        if (!record?.id) {
                          handleBlurSave(rowIndex, record);
                          return;
                        }
                        try {
                          // Filter out deleted fields before validation
                          const activeHeaders = tableHeaders?.filter(
                            (header) => !header?.deletedAt,
                          );
                          const allValues = form?.getFieldsValue();
                          const rowFieldKeys = Object.keys(allValues)?.filter(
                            (key) => key?.startsWith(`${rowIndex}`),
                          );
                          const activeFieldKeys = rowFieldKeys?.filter(
                            (key) => {
                              const fieldName = key?.split('_')?.[1];
                              return activeHeaders?.some(
                                (header) => header?.dataIndex === fieldName,
                              );
                            },
                          );

                          await form.validateFields(activeFieldKeys, {
                            validateOnly: true,
                          });
                          handleBlurSave(rowIndex, record);
                        } catch (error) {
                          handleTableRouterPromptUpdate(true);
                          showRouterPrompt(true);
                        }
                      }
                    },
                    isDisabled: isDisabled || field?.deletedAt,
                    handleFieldChange,
                    handleBlurSave,
                    handleFileListRefUpdate,
                    form,
                    tableHeaders,
                    fileListRef,
                    deletingFile,
                    setDeletingFile,
                    uploadKey,
                    handleOnChange,
                    handleUploadRemove: async (file) => {
                      try {
                        if (deletingFile) return false;
                        setDeletingFile(true);
                        const fieldName = `${rowIndex}_${toCamelCase(field?.name)}`;
                        modifiedFieldsRef?.current?.add(fieldName);
                        const currentFileList = [
                          ...(fileListRef?.current[fieldName] || []),
                        ];
                        const updatedFileList = currentFileList?.filter(
                          (f) => f?.uid !== file?.uid,
                        );
                        fileListRef.current[fieldName] = updatedFileList;
                        const currentField = fieldsData?.current?.[rowIndex];
                        if (!currentField) {
                          setDeletingFile(false);
                          return true;
                        }
                        const currentFiles =
                          currentField[toCamelCase(field?.name)] || [];
                        const remainingFiles = currentFiles?.filter((item) => {
                          return item?.key !== file?.key;
                        });
                        fieldsData.current[rowIndex] = {
                          ...currentField,
                          [toCamelCase(field?.name)]: remainingFiles,
                        };
                        form?.setFieldValue(fieldName, remainingFiles);
                        form?.validateFields([fieldName]);

                        const isFieldRequired =
                          field?.isRequired && !record?.isEmptyRow;
                        const willBeEmpty = !remainingFiles?.length;

                        if (isFieldRequired && willBeEmpty) {
                          setDeletingFile(false);
                          return false;
                        }

                        if (file?.key && imageKeys?.current?.[fieldName]) {
                          const fileNameWithoutSequence = file?.key?.replace(
                            REGEX.ID_WITH_PARENTHESES_AND_NUMBER,
                            ' $1',
                          );
                          imageKeys.current[fieldName] = imageKeys?.current?.[
                            fieldName
                          ]?.filter(
                            (key) =>
                              key?.replace(
                                REGEX.ID_WITH_PARENTHESES_AND_NUMBER,
                                ' $1',
                              ) !== fileNameWithoutSequence,
                          );
                        }

                        if (record?.id && (!isFieldRequired || !willBeEmpty)) {
                          const currentColumn = tableHeaders?.find(
                            (col) =>
                              col?.dataIndex === toCamelCase(field?.name),
                          );
                          if (currentColumn) {
                            const existingKeys = currentFiles
                              ?.filter((f) => f?.key && f?.key !== file?.key)
                              ?.map((f) => f?.key);
                            const newKeys = imageKeys?.current[fieldName] || [];
                            const allFileKeys = [
                              ...new Set([...existingKeys, ...newKeys]),
                            ];
                            const finalFileKeys = allFileKeys?.filter(
                              (key) =>
                                key !== file?.key &&
                                (!file?.name || !key?.includes(file?.name)),
                            );

                            try {
                              await updateRegisterLogFormFieldEntry({
                                variables: {
                                  updateRegisterLogFormFieldEntryId: record?.id,
                                  data: {
                                    isNewRow: false,
                                    responses: [
                                      {
                                        registerFormFieldId: currentColumn?.id,
                                        value: take(finalFileKeys, 3)?.join(
                                          ',',
                                        ),
                                      },
                                    ],
                                  },
                                },
                              });
                              // Track this updated row for badge indicator
                              setLocallyModifiedRowIds((prev) => {
                                const newSet = new Set(prev);
                                newSet?.add(record?.id?.toString());
                                return newSet;
                              });
                            } catch (error) {
                              message.destroy();
                              message.error('Failed to remove file');
                              setDeletingFile(false);
                              return error;
                            }
                          }
                        }

                        await form?.validateFields();
                        setDeletingFile(false);
                        setUploadKey((prev) => prev + 1);
                        return true;
                      } catch (error) {
                        handleTableRouterPromptUpdate(true);
                        showRouterPrompt(true);
                        return error;
                      }
                    },
                    handlePreview,
                    handleTableRouterPromptUpdate,
                  }),
                  render: (text) => {
                    const content = (() => {
                      if (Array.isArray(text)) {
                        if (
                          text?.length > 0 &&
                          text?.some((item) => item?.key)
                        ) {
                          return (
                            <div className="d-flex align-center  gap-8">
                              <Image
                                src={
                                  text[0]?.value ||
                                  text[0]?.thumbUrl ||
                                  text[0]?.url
                                }
                                preview={false}
                                height={50}
                                width={50}
                                fallback={FALLBACK_PDF_URL}
                              />
                              {text?.length - 1 > 0 && (
                                <span className="ml-5 pointer extra-count">
                                  +{text?.length - 1}
                                </span>
                              )}
                            </div>
                          );
                        }
                        if (!text?.length) {
                          return '-';
                        }
                        return text?.join(',');
                      }
                      if (
                        field?.type === FIELD_TYPES.DATE &&
                        moment(text, DEFAULTDATEFORMAT)?.isValid()
                      ) {
                        return moment(text, DEFAULTDATEFORMAT)?.format(
                          DEFAULTDATEFORMAT,
                        );
                      }
                      if (field?.type === FIELD_TYPES.TIME) {
                        if (moment?.isMoment(text) && text?.isValid()) {
                          return text?.format(TIME_12_HOUR_FORMAT);
                        }
                        if (
                          moment(text, TIME_24_HOUR_FORMAT, true)?.isValid()
                        ) {
                          return moment(text, TIME_24_HOUR_FORMAT)?.format(
                            TIME_12_HOUR_FORMAT,
                          );
                        }
                      }
                      if (includes(INVALID_FIELD_VALUES, text)) {
                        return '-';
                      }
                      return text;
                    })();

                    return field?.deletedAt ? (
                      <div className="deleted-field">{content}</div>
                    ) : (
                      content
                    );
                  },
                  title: (
                    <div className={field?.deletedAt ? 'deleted-field' : ''}>
                      <div className="width-percent-100 d-flex align-center justify-between gap-8">
                        {isEditControl && registerId && isEditable && (
                          <DragHandle />
                        )}
                        <div className="d-flex align-center">
                          {field?.isRequired && (
                            <div className="required-mark">*</div>
                          )}
                          <div className="mr-5">
                            <FieldIcon fieldType={field?.type} />
                          </div>
                          <div className="mr-5">
                            {field?.name}
                            {field?.deletedAt && ' (Deleted)'}
                          </div>
                          {!isEditControl &&
                            renderAggregateDropdown(
                              field?.id,
                              field?.type,
                              field?.calculationResultType,
                            )}
                        </div>
                        {isEditControl && (
                          <div className="d-flex">
                            {isEditable ? (
                              <>
                                <EditButton
                                  className="mr-10"
                                  onClick={() => handleEditField(field)}
                                />
                                <DeleteButton
                                  onClick={() => {
                                    if (tableHeaders?.length === 1) {
                                      message.destroy();
                                      message.error(
                                        'There must be at-least 1 form field!',
                                      );
                                    } else if (!isAuditor || isDisabled) {
                                      setSelectedField(field);
                                      setShowDeleteModal(true);
                                    }
                                  }}
                                />
                              </>
                            ) : (
                              <div
                                className="pointer text-underline mr-10"
                                onClick={() => handleEditField(field)}
                              >
                                View
                              </div>
                            )}
                          </div>
                        )}
                      </div>
                      {!isEditControl && aggregateResults?.[field?.id] && (
                        <div className="aggregate-results">
                          {Object?.entries(aggregateResults?.[field?.id])?.map(
                            ([operation, result]) => (
                              <div
                                key={operation}
                                className="aggregate-result-item"
                              >
                                <span className="aggregate-function-name">
                                  {API_TO_AGGREGATE_FUNCTION_LABELS[operation]}:
                                </span>
                                <span className="aggregate-function-value">
                                  {result}
                                </span>
                              </div>
                            ),
                          )}
                        </div>
                      )}
                    </div>
                  ),
                  className: (() => {
                    if (field?.deletedAt) return 'deleted-field-column';
                    if (isEditControl) return 'drag-visible';
                    return '';
                  })(),
                };
              })
            : []),
          // eslint-disable-next-line no-nested-ternary
          ...(isEditControl && isEditable
            ? [
                {
                  key: 'addField',
                  id: 'addField',
                  title: (
                    <CanPerform
                      action={ALLOWED_ACTION_KEYS.ADD_PROJECT_REGISTER}
                      type={ALLOWED_ACTION_TYPE.BOTH}
                    >
                      <Button
                        type="link"
                        icon={<PlusOutlined />}
                        onClick={() => {
                          setSelectedField();
                          setShowModal(true);
                        }}
                      >
                        Add Field
                      </Button>
                    </CanPerform>
                  ),
                  dataIndex: 'addField',
                  render: () => null,
                  disableSort: true,
                  className: '',
                },
              ]
            : []),
          ...(!isEditControl &&
          CanPerform({
            action: ALLOWED_ACTION_KEYS.ADD_REGISTER_LOG,
            type: ALLOWED_ACTION_TYPE.BOTH,
          }) &&
          includes(
            [REGISTER_LOG_STATUS.REDO, REGISTER_LOG_STATUS.APPROVED],
            historyData?.approvalStatus,
          )
            ? [
                {
                  key: 'approverComment',
                  id: 'approverComment',
                  dataIndex: 'approverComment',
                  width: 100,
                  title: 'Approver Comment',
                  fixed: isDesktopViewport && 'right',
                  render: getApproverComment,
                },
              ]
            : []),
          ...(!isEditControl &&
          CanPerform({
            action: ALLOWED_ACTION_KEYS.ADD_REGISTER_LOG,
            type: ALLOWED_ACTION_TYPE.BOTH,
          })
            ? [
                {
                  key: 'actions',
                  id: 'actions',
                  dataIndex: 'action',
                  align: 'center',
                  width: 85,
                  title: 'Actions',
                  fixed: isDesktopViewport && 'right',
                  render: (_, record, index) => (
                    <div className="d-flex align-center width-percent-100">
                      <Button
                        type="link"
                        disabled={isDisabled || record?.isEmptyRow}
                        icon={<DeleteButton />}
                        className={clsx(
                          isDisabled || record?.isEmptyRow
                            ? 'disabled-button'
                            : 'pointer',
                          'entry-delete-button',
                        )}
                        onClick={(e) => {
                          e?.stopPropagation();
                          handleDeleteRecord(record, index);
                        }}
                      />
                    </div>
                  ),
                },
              ]
            : []),
        ],
        [
          filteredTableHeaders,
          isEditControl,
          isEditable,
          isMobileViewport,
          isOptionsFocused,
          isMultiSelectOptionsFocused,
          isDateFocused,
          isTimeFocused,
          editingKeys,
          isProjectAdminOrInspector,
          aggregateResults,
          totalTableDataCount,
          isRowUpdatedAfterHistory,
        ],
      );

      const handleDragEnd = (event) => {
        const { active, over } = event;
        if (active && over) {
          updateRegisterFormFieldSequences({
            variables: {
              data: {
                registerId,
                registerFormFieldId: active?.id,
                sequence: over?.data?.current?.sortable?.index + 1,
              },
            },
          });
        }
        if (!active || !over || active?.id === over?.id) {
          return;
        }
        const oldIndex = tableHeaders?.findIndex(
          (col) => col?.id === active?.id,
        );
        const newIndex = tableHeaders?.findIndex((col) => col?.id === over?.id);
        if (oldIndex !== -1 && newIndex !== -1) {
          const newHeaders = arrayMove(tableHeaders, oldIndex, newIndex);
          setTableHeaders(newHeaders);
          const newTableColumns = arrayMove(tableColumns, oldIndex, newIndex);
          setTableColumns(newTableColumns);
        }
      };

      return (
        <>
          {!isEditControl && (
            <Portal portalId="register-table-search">
              <SearchComponent
                className={
                  isDesktopViewport ? 'width-200' : 'width-percent-100 mt-10'
                }
                getData={onSearchChange}
                placeholder="Search"
                defaultValue={searchValue}
              />
            </Portal>
          )}
          <DndContext
            modifiers={[restrictToHorizontalAxis]}
            onDragEnd={handleDragEnd}
            sensors={sensors}
          >
            <SortableContext
              items={tableHeaders?.map((col) => col?.id)}
              strategy={horizontalListSortingStrategy}
            >
              {!tableHeaders?.length > 0 && !isEditControl ? (
                <Empty />
              ) : (
                <>
                  {
                    //! giving inline style as need to generate image from this div
                  }
                  <div style={{ opacity: 0, height: 0 }}>
                    <div
                      id={ELEMENT_IDS.TIME_STAMP}
                      style={{
                        background: '#000',
                        color: '#3366ff',
                        width: 'max-content',
                      }}
                    >
                      {`${moment()?.format(DATETIMEWITHDIVIDE)} - ${currentProjectDetails?.timeZone}`}
                    </div>
                  </div>
                  {registerLog &&
                  !isEditControl &&
                  !showAll &&
                  totalTableDataCount === 0 &&
                  registerLog?.status === REGISTER_LOG_STATUS.REDO ? (
                    <Alert
                      message='Only the approver&apos;s commented entries are shown. Tap "Show All Entries" to see the full register log'
                      type="warning"
                      showIcon
                      className="no-field-warning mb-15"
                    />
                  ) : (
                    <Form
                      form={form}
                      component={false}
                      onFieldsChange={(changedFields) => {
                        // Skip if we're re-indexing fields after row deletion
                        if (isReindexingRef.current) {
                          return;
                        }

                        if (!isFormChanged) {
                          setIsFormChanged(true);
                          const showTableFieldsPromptRef =
                            showTableFieldsPrompt;
                          if (
                            showTableFieldsPromptRef &&
                            showTableFieldsPromptRef.current !== undefined
                          ) {
                            showTableFieldsPromptRef.current = true;
                          }
                          showRouterPrompt(true);
                        }
                        if (onTableDirty) {
                          onTableDirty();
                        }
                        if (onTableErrorCheck) {
                          const errors = form?.getFieldsError();
                          let hasErrors = errors?.some(
                            (field) => field?.errors?.length > 0,
                          );
                          if (
                            !hasErrors &&
                            changedFields &&
                            changedFields?.length > 0
                          ) {
                            const fieldName = changedFields?.[0]?.name[0];
                            const rowIndex = fieldName?.split('_')[0];
                            if (rowIndex) {
                              const allValues = form?.getFieldsValue();
                              // Filter out deleted fields before validation
                              const activeHeaders = tableHeaders?.filter(
                                (header) => !header?.deletedAt,
                              );
                              const requiredFields = activeHeaders?.filter(
                                (field) => field?.isRequired,
                              );

                              const hasMissingMandatory = requiredFields?.some(
                                (field) => {
                                  const fieldKey = `${rowIndex}_${field?.dataIndex}`;
                                  const fieldValue = allValues?.[fieldKey];
                                  const isEmpty =
                                    !fieldValue ||
                                    fieldValue === '' ||
                                    fieldValue === '-' ||
                                    (Array?.isArray(fieldValue) &&
                                      fieldValue?.length === 0);
                                  return isEmpty;
                                },
                              );
                              if (hasMissingMandatory) {
                                hasErrors = true;
                              }
                            }
                          }
                          onTableErrorCheck(hasErrors);
                        }
                      }}
                    >
                      <div ref={tableRef}>
                        <CommonTable
                          components={{
                            header: { cell: Columns },
                            body: { cell: EditableCell },
                          }}
                          data={tableData}
                          className="register-table"
                          columns={columns}
                          loading={loading && !isLoading}
                          scroll={{
                            x: 'max-content',
                            ...(!isEditControl && { y: 55 * 10 }),
                          }}
                          onRow={(record, index) => ({
                            onClick: () => {
                              if (
                                isMobileViewport &&
                                !isAuditor &&
                                isProjectAdminOrInspector &&
                                !isDisabled
                              ) {
                                setRowRecord(record);
                                setFieldIndex(index);
                                setShowFillModal(true);
                              } else if (!isEditing(record)) {
                                setEditingKeys((prev) => {
                                  const allErrors = form?.getFieldsError();
                                  const rowKeysWithErrors = new Set();
                                  allErrors?.forEach(({ name, errors }) => {
                                    if (errors?.length > 0) {
                                      const fieldKey = Array?.isArray(name)
                                        ? name?.[0]
                                        : name;
                                      const rowIndex =
                                        fieldKey?.split('_')?.[0];
                                      if (
                                        rowIndex !== undefined &&
                                        fieldsData?.current?.[rowIndex]
                                      ) {
                                        rowKeysWithErrors.add(
                                          fieldsData?.current?.[rowIndex]?.key,
                                        );
                                      }
                                    }
                                  });
                                  const newSet = new Set(
                                    [...prev].filter((key) =>
                                      fieldsData?.current?.some(
                                        (row) =>
                                          row?.key === key &&
                                          (row?.isEmptyRow ||
                                            rowKeysWithErrors?.has(key)),
                                      ),
                                    ),
                                  );
                                  newSet.add(record?.key);
                                  return newSet;
                                });

                                const formFields = {};
                                Object?.keys(record)?.forEach((key) => {
                                  if (key !== 'key' && key !== 'id') {
                                    const field = tableHeaders?.find(
                                      (header) => header?.dataIndex === key,
                                    );
                                    if (
                                      field?.type === FIELD_TYPES.CALCULATION &&
                                      record?.[key]
                                    ) {
                                      formFields[`${index}_${key}`] =
                                        record?.[key];
                                    } else if (
                                      field?.type === FIELD_TYPES.OPTIONS
                                    ) {
                                      formFields[`${index}_${key}`] =
                                        record?.[key] || undefined;
                                    } else if (field?.type === FIELD_TYPES.DATE) {
                                      // Ensure date value is a valid moment object
                                      const dateValue = record?.[key];
                                      if (
                                        moment?.isMoment(dateValue) &&
                                        dateValue?.isValid()
                                      ) {
                                        formFields[`${index}_${key}`] = dateValue;
                                      } else if (
                                        dateValue &&
                                        typeof dateValue === 'string'
                                      ) {
                                        const parsed = moment(
                                          dateValue,
                                          DEFAULTDATEFORMAT,
                                          true,
                                        );
                                        formFields[`${index}_${key}`] =
                                          parsed?.isValid() ? parsed : undefined;
                                      } else {
                                        formFields[`${index}_${key}`] = undefined;
                                      }
                                    } else if (field?.type === FIELD_TYPES.TIME) {
                                      // Ensure time value is a valid moment object
                                      const timeValue = record?.[key];
                                      if (
                                        moment?.isMoment(timeValue) &&
                                        timeValue?.isValid()
                                      ) {
                                        formFields[`${index}_${key}`] = timeValue;
                                      } else if (
                                        timeValue &&
                                        typeof timeValue === 'string'
                                      ) {
                                        const parsed = moment(
                                          timeValue,
                                          TIME_24_HOUR_FORMAT,
                                          true,
                                        );
                                        formFields[`${index}_${key}`] =
                                          parsed?.isValid() ? parsed : undefined;
                                      } else {
                                        formFields[`${index}_${key}`] = undefined;
                                      }
                                    } else {
                                      formFields[`${index}_${key}`] =
                                        Array?.isArray(record[key])
                                          ? record?.[key] || []
                                          : record?.[key] || undefined;
                                    }
                                  }
                                });
                                form.setFieldsValue(formFields);
                              }
                            },
                          })}
                          rowClassName="editable-row"
                          onScroll={handleScroll}
                        />
                      </div>
                    </Form>
                  )}

                  {!isEditControl &&
                    tableData?.length > 0 &&
                    historyData?.isLatest &&
                    registerLog?.status === REGISTER_LOG_STATUS?.REDO && (
                      <div className="mt-15">
                        <Alert
                          message={
                            <span>
                              <strong>Note:</strong>{' '}
                              <Badge dot color="#1890ff" className="mr-5" />
                              These entries were updated/added after redo.
                            </span>
                          }
                          type="info"
                          className="no-field-warning"
                          showIcon={false}
                        />
                      </div>
                    )}

                  {!isEditControl &&
                    tableData?.length > 0 &&
                    historyData?.comments?.length > 0 && (
                      <div className="mt-15 d-flex justify-end">
                        <Button
                          type="primary"
                          shape="round"
                          onClick={() => {
                            const formattedComments =
                              historyData?.comments?.map((comment) => ({
                                remark: comment?.commentText || '-',
                                assets: comment?.assetUrls || [],
                              }));
                            setSelectedAdditionalComments(formattedComments);
                            setShowAdditionalCommentsModal(true);
                          }}
                        >
                          View Additional Comments
                        </Button>
                      </div>
                    )}
                </>
              )}
            </SortableContext>
          </DndContext>
          {showModal && (
            <AddFieldModal
              tableHeaders={tableHeaders}
              isDisabled={isAuditor || !isEditable}
              tableColumns={tableColumns}
              setTableColumns={setTableColumns}
              isEditControl={isEditControl}
              selectedField={selectedField}
              isUpdate={!!selectedField}
              numericFields={numericFields}
              setNumericFields={setNumericFields}
              showModal={showModal}
              setShowModal={setShowModal}
              setSelectedField={setSelectedField}
              handleRefetch={handleRegisterRefetch}
            />
          )}
          {showFillModal && (
            <AddEditTableFieldModal
              tableForm={form}
              tableHeaders={tableHeaders}
              tableData={tableData}
              setTableData={setTableData}
              fieldsData={fieldsData}
              rowRecord={rowRecord}
              setRowRecord={setRowRecord}
              fieldIndex={fieldIndex}
              setFieldIndex={setFieldIndex}
              showModal={showFillModal}
              setShowModal={setShowFillModal}
              handlePreview={handlePreview}
              fileListRef={fileListRef}
              isFieldClicked={isFieldClicked}
              handleTableRouterPromptUpdate={handleTableRouterPromptUpdate}
              handleFieldsDataRefUpdate={handleFieldsDataRefUpdate}
              handleFileListRefUpdate={handleFileListRefUpdate}
              editingKeys={editingKeys}
              setEditingKeys={setEditingKeys}
              handleRegisterRefetch={handleRegisterRefetch}
              handleRegisterLogDetailRefetch={handleRegisterLogDetailRefetch}
              handleHisotryRefetch={handleHisotryRefetch}
              onTableDirty={onTableDirty}
              onTableErrorCheck={onTableErrorCheck}
              modifiedFieldsRef={modifiedFieldsRef}
              handleSaveAsDraft={handleSaveAsDraft}
              showTableFieldConfirmModal={showTableFieldConfirmModal}
              setShowTableFieldConfirmModal={setShowTableFieldConfirmModal}
            />
          )}
          {showDeleteModal && (
            <DeleteTableFieldModal
              showModal={showDeleteModal}
              setShowModal={setShowDeleteModal}
              selectedField={selectedField}
              setSelectedField={setSelectedField}
              handleRefetch={handleRegisterRefetch}
            />
          )}
          {showDeleteRecordModal && (
            <DeleteFormFieldRecordModal
              showModal={showDeleteRecordModal}
              setShowModal={setShowDeleteRecordModal}
              selectedRecordToDelete={selectedRecordToDelete}
              setSelectedRecordToDelete={setSelectedRecordToDelete}
              fieldIndex={fieldIndex}
              tableHeaders={tableHeaders}
              tableData={tableData}
              setTableData={setTableData}
              fieldsData={fieldsData}
              form={form}
              showRouterPrompt={showRouterPrompt}
              fileListRef={fileListRef}
              handleFieldsDataRefUpdate={handleFieldsDataRefUpdate}
              handleFileListRefUpdate={handleFileListRefUpdate}
              refetchAggregateStats={refetchAggregateStats}
              handleRegisterLogDetailRefetch={handleRegisterLogDetailRefetch}
              modifiedFieldsRef={modifiedFieldsRef?.current}
              onTableSave={onTableSave}
            />
          )}
          {showCommentModal && selectedCommentRecord && (
            <RegisterCommentModal
              showModal={showCommentModal}
              setShowModal={() => {
                setShowCommentModal(false);
                setSelectedCommentRecord(null);
              }}
              comment={selectedCommentRecord}
            />
          )}
          {showAdditionalCommentsModal && (
            <AdditionalCommentsModal
              showModal={showAdditionalCommentsModal}
              setShowModal={setShowAdditionalCommentsModal}
              additionalComments={selectedAdditionalComments}
            />
          )}
        </>
      );
    },
  ),
);

export default RegisterFieldsTableList;
